// The design system's icons (design/components/bundle.js): 24 px grid, 1.75 px round-capped strokes, drawn to
// match SF Symbols regular; transport glyphs and `more` are filled. Glyphs the system lacks follow Lucide at the
// same stroke width (brand book › Iconography), marked below.
import type { SVGProps } from 'react';

type Part = ['path' | 'circle' | 'rect', Record<string, string | number>];
const F = { fill: 'currentColor', stroke: 'none' };

const ICONS = {
  play: [['path', { d: 'M8 5.8v12.4a1 1 0 0 0 1.53.85l9.7-6.2a1 1 0 0 0 0-1.7l-9.7-6.2A1 1 0 0 0 8 5.8z', ...F }]],
  pause: [['rect', { x: 6.5, y: 5, width: 4, height: 14, rx: 1.2, ...F }], ['rect', { x: 13.5, y: 5, width: 4, height: 14, rx: 1.2, ...F }]],
  stop: [['rect', { x: 6, y: 6, width: 12, height: 12, rx: 2.5, ...F }]],
  plus: [['path', { d: 'M12 5v14M5 12h14' }]],
  check: [['path', { d: 'M5 12.5l4.5 4.5L19 7.5' }]],
  clock: [['circle', { cx: 12, cy: 12, r: 8.5 }], ['path', { d: 'M12 7.5V12l3 2' }]],
  hourglass: [['path', { d: 'M7 4h10M7 20h10M8.5 4v2.2c0 2.1 3.5 3.6 3.5 5.8s-3.5 3.7-3.5 5.8V20M15.5 4v2.2c0 2.1-3.5 3.6-3.5 5.8s3.5 3.7 3.5 5.8V20' }]],
  moon: [['path', { d: 'M19 14.2A7.5 7.5 0 0 1 9.8 5a7.5 7.5 0 1 0 9.2 9.2z' }]],
  alert: [['path', { d: 'M10.3 5.2L3.6 17a2 2 0 0 0 1.7 3h13.4a2 2 0 0 0 1.7-3L13.7 5.2a2 2 0 0 0-3.4 0z' }], ['path', { d: 'M12 10v3.5M12 16.6v.1' }]],
  terminal: [['rect', { x: 3.5, y: 5, width: 17, height: 14, rx: 3 }], ['path', { d: 'M7.5 10l2.5 2-2.5 2M12.5 14.5h4' }]],
  user: [['circle', { cx: 12, cy: 8.5, r: 3.5 }], ['path', { d: 'M5 19.5c1.2-3.1 3.8-5 7-5s5.8 1.9 7 5' }]],
  plug: [['path', { d: 'M9 3.5v4M15 3.5v4M6.5 7.5h11v3a5.5 5.5 0 0 1-11 0zM12 16v4.5' }]],
  chevron: [['path', { d: 'M9.5 6l6 6-6 6' }]],
  folder: [['path', { d: 'M3.5 7.5a2 2 0 0 1 2-2h3.8l2 2h7.2a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2z' }]],
  branch: [['circle', { cx: 7, cy: 6, r: 2 }], ['circle', { cx: 7, cy: 18, r: 2 }], ['circle', { cx: 17, cy: 7, r: 2 }], ['path', { d: 'M7 8v8M17 9c0 4.5-10 2.5-10 7' }]],
  chart: [['path', { d: 'M5 19v-7M12 19V5M19 19v-10' }]],
  calendar: [['rect', { x: 4, y: 5.5, width: 16, height: 14.5, rx: 3 }], ['path', { d: 'M4 10h16M8.5 3.5v4M15.5 3.5v4' }]],
  more: [['circle', { cx: 6, cy: 12, r: 1.4, ...F }], ['circle', { cx: 12, cy: 12, r: 1.4, ...F }], ['circle', { cx: 18, cy: 12, r: 1.4, ...F }]],
  search: [['circle', { cx: 11, cy: 11, r: 6 }], ['path', { d: 'M20 20l-4.5-4.5' }]],
  arrowUp: [['path', { d: 'M12 19V5M6.5 10.5L12 5l5.5 5.5' }]],
  arrowDown: [['path', { d: 'M12 5v14M6.5 13.5L12 19l5.5-5.5' }]],
  euro: [['path', { d: 'M17.5 6.8A6.5 6.5 0 1 0 17.5 17.2M5 10.5h8M5 13.5h8' }]],
  gear: [['circle', { cx: 12, cy: 12, r: 3 }], ['path', { d: 'M12 3.5v2.2M12 18.3v2.2M20.5 12h-2.2M5.7 12H3.5M18 6l-1.6 1.6M7.6 16.4L6 18M18 18l-1.6-1.6M7.6 7.6L6 6' }]],
  // Lucide, same stroke: menu, x, chevron-left, chevron-down, share, download, pencil, trash-2, undo-2, globe, file-text,
  // copy, laptop, server, log-out, shield, sun, monitor, link.
  menu: [['path', { d: 'M4 6.5h16M4 12h16M4 17.5h16' }]],
  close: [['path', { d: 'M6 6l12 12M18 6L6 18' }]],
  chevronLeft: [['path', { d: 'M14.5 6l-6 6 6 6' }]],
  chevronDown: [['path', { d: 'M6 9.5l6 6 6-6' }]],
  share: [['path', { d: 'M12 3.5v11M7.5 8L12 3.5 16.5 8M5 12.5v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5' }]],
  download: [['path', { d: 'M12 3.5v11M7.5 10l4.5 4.5 4.5-4.5M5 16.5v1a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1' }]],
  pencil: [['path', { d: 'M16.5 4.5a2.1 2.1 0 0 1 3 3L8 19l-4 1 1-4z' }]],
  trash: [['path', { d: 'M4.5 7h15M9.5 7V5a1.5 1.5 0 0 1 1.5-1.5h2A1.5 1.5 0 0 1 14.5 5v2M6.5 7l.8 11.2a2 2 0 0 0 2 1.8h5.4a2 2 0 0 0 2-1.8L17.5 7' }]],
  undo: [['path', { d: 'M9 14L4.5 9.5 9 5M4.5 9.5h10a5 5 0 0 1 0 10H11' }]],
  network: [['circle', { cx: 12, cy: 12, r: 8.5 }], ['path', { d: 'M3.5 12h17M12 3.5a13 13 0 0 1 0 17M12 3.5a13 13 0 0 0 0 17' }]],
  doc: [['path', { d: 'M14 3.5H7.5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V8z' }], ['path', { d: 'M14 3.5V8h4.5M9 12.5h6M9 16h6' }]],
  copy: [['rect', { x: 8.5, y: 8.5, width: 11.5, height: 11.5, rx: 2 }], ['path', { d: 'M15.5 8.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7.5a2 2 0 0 0 2 2h2.5' }]],
  laptop: [['rect', { x: 5, y: 5, width: 14, height: 10, rx: 2 }], ['path', { d: 'M3 19h18' }]],
  server: [['rect', { x: 4, y: 4.5, width: 16, height: 6.5, rx: 2 }], ['rect', { x: 4, y: 13, width: 16, height: 6.5, rx: 2 }], ['path', { d: 'M8 7.75h.01M8 16.25h.01' }]],
  logout: [['path', { d: 'M9.5 20H6.5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h3M15.5 16.5L20 12l-4.5-4.5M20 12H9.5' }]],
  shield: [['path', { d: 'M12 3.5l7 2.8v5.2c0 4.3-3 7.7-7 9-4-1.3-7-4.7-7-9V6.3z' }]],
  sun: [['circle', { cx: 12, cy: 12, r: 3.8 }], ['path', { d: 'M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6L7 7M17 17l1.4 1.4M5.6 18.4L7 17M17 7l1.4-1.4' }]],
  monitor: [['rect', { x: 3.5, y: 4.5, width: 17, height: 11.5, rx: 2 }], ['path', { d: 'M8.5 20h7M12 16v4' }]],
  link: [['path', { d: 'M10 13.5a4.5 4.5 0 0 0 6.4.5l2.6-2.6a4.5 4.5 0 0 0-6.4-6.4l-1.5 1.5M14 10.5a4.5 4.5 0 0 0-6.4-.5L5 12.6a4.5 4.5 0 0 0 6.4 6.4l1.5-1.5' }]],
} satisfies Record<string, Part[]>;

export type IconName = keyof typeof ICONS;

export interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  /** Spoken label; without one the icon is decorative (aria-hidden). */
  label?: string;
  className?: string;
}

export function Icon({ name, size = 20, strokeWidth = 1.75, label, className }: IconProps) {
  const parts = ICONS[name] as Part[];
  const a11y: SVGProps<SVGSVGElement> = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true };
  return (
    <svg className={className ? `tl-icon ${className}` : 'tl-icon'} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...a11y}>
      {parts.map(([tag, attrs], i) => {
        const Tag = tag;
        return <Tag key={i} {...attrs} />;
      })}
    </svg>
  );
}
